import type { SystemLossDerivedRow } from 'types';

import type { EvaluationInputComponentProps } from '../evaluationClientRegistry';

interface ComparisonRow {
  reportedLabel: string;
  reportedValue: string;
  expectedLabel: string;
  expectedValue: string;
}

const formatPercent = (value: number, decimals: number): string => `${Number(value.toFixed(decimals))}%`;

const describeMissingExpected = (row: SystemLossDerivedRow): string => {
  const location = row.locationLabel ?? 'this location';
  return row.thresholdStatus === 'countyUnresolved'
    ? `Unavailable: county is unresolved for ${location}`
    : `Unavailable: no published threshold for ${location}`;
};

const buildComparison = (row: SystemLossDerivedRow): ComparisonRow | null => {
  const isResolved = row.thresholdStatus === 'resolved';

  if (row.totalSystemLossPercent !== null) {
    const expected = row.expectedMinSystemLossPercent;
    return {
      reportedLabel: 'Reported total system loss',
      reportedValue: formatPercent(row.totalSystemLossPercent, 2),
      expectedLabel: 'Minimum system loss',
      expectedValue: isResolved && expected !== null ? formatPercent(expected, 1) : describeMissingExpected(row),
    };
  }

  if (row.snowLossPercent !== null) {
    const expected = row.expectedSnowLossPercent;
    let expectedValue: string;
    if (!isResolved) {
      expectedValue = describeMissingExpected(row);
    } else if (expected === null) {
      expectedValue = 'Not applicable';
    } else {
      expectedValue = formatPercent(expected, 1);
    }
    return {
      reportedLabel: 'Reported snow loss',
      reportedValue: formatPercent(row.snowLossPercent, 2),
      expectedLabel: 'Expected snow loss',
      expectedValue,
    };
  }

  return null;
};

interface LabeledValueProps {
  label: string;
  value: string;
}

const LabeledValue = ({ label, value }: LabeledValueProps) => (
  <div className="flex flex-1 flex-col gap-2">
    <span className="body-200 text-text-secondary">{label}</span>
    <span className="body-bold-300 text-text-primary">{value}</span>
  </div>
);

// Display-only: the reviewer's decision select carries the outcome, so this never calls onChange.
export const SystemLossEvaluationInput: React.FC<EvaluationInputComponentProps> = ({ label, view }) => {
  const derived = view?.rows[0]?.derived as SystemLossDerivedRow | undefined;
  if (!view || !derived) return null;

  const comparison = buildComparison(derived);

  return (
    <div className="flex flex-col gap-8 py-4">
      <span className="body-bold-300 text-text-primary">{label}</span>

      {derived.locationLabel && <LabeledValue label="Location" value={derived.locationLabel} />}

      {comparison ? (
        <div className="flex flex-wrap gap-16">
          <LabeledValue label={comparison.reportedLabel} value={comparison.reportedValue} />
          <LabeledValue label={comparison.expectedLabel} value={comparison.expectedValue} />
        </div>
      ) : (
        <span className="body-300 text-text-secondary">Value not found</span>
      )}
    </div>
  );
};
